<template>
  <div class="col-12">
    <div class="card mb-5 " style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px;">
        <div class="bg-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
              {{ t('students.edit_student_title') }}
            </h6>
          </div>
        </div>
      </div>
   
      <!-- Loading state -->
      <LoadingSpinner v-if="isLoading" :message="t('students.loading_student')" />

      <!-- Content when loaded -->
      <div v-else class="card-body px-0 mx-2">
        <div class="row justify-content-center">
          <div class="col-12">
            <StudentForm 
              ref="studentForm"
              v-if="student"
              :title="t('students.edit_student_title')"
              :submit-text="t('students.save_changes')"
              :is-edit-mode="true"
              :initial-data="formattedStudentData"
              :student-id="student.id" 
              @submit="handleSubmit" 
              @cancel="handleCancel"
            />
            <div v-else class="text-center py-4">
              <div class="alert alert-danger">
                {{ t('students.not_found') }}
              </div>
              <button class="btn btn-secondary" @click="handleCancel">
                {{ t('students.back_to_list') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import StudentForm from '@/components/features/students/forms/StudentForm.vue';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import api from "@/api"

const { t } = useI18n();
const toast = useToast();
const route = useRoute();
const router = useRouter();
const studentForm = ref(null);
const isSubmitting = ref(false);
const isLoading = ref(true);
const student = ref(null);

// Format the student data for the form
const formattedStudentData = computed(() => {
  if (!student.value) return null;
  
  const assignment = student.value.current_assignment ||
                   (student.value.class_groups && student.value.class_groups[0]);
  
  return {
    ...student.value,
    class_id: assignment?.class?.id || null,
    group_id: assignment?.group?.id || assignment?.id || null,
    nationality_id: student.value.nationality?.id || '',
    register_date: student.value.register_date || new Date().toISOString().split('T')[0]
  };
});

const handleSubmit = async (formData) => {
  if (isSubmitting.value) return;
  isSubmitting.value = true;
  
  try {
    const formDataToSend = new FormData();
    
    // Append all fields including nested objects
    for (const key in formData) {
      if (formData[key] !== null && formData[key] !== undefined) {
        if (key === 'image' && formData[key] instanceof File) {
          formDataToSend.append('image', formData[key]);
        } else {
          // Handle nested objects (like nationality)
          if (typeof formData[key] === 'object') {
            formDataToSend.append(key, formData[key].id);
          } else {
            formDataToSend.append(key, formData[key]);
          }
        }
      }
    }

    // Use PUT method
    formDataToSend.append('_method', 'PUT');

    await api.post(`/students/${route.params.id}`, formDataToSend, {
      headers: { 
        "Content-Type": "multipart/form-data",
      }
    });
    
    toast.success(t('students.update_success'));
    router.push('/students');
  } catch (error) {
    console.error('Error updating student:', error);
    
    if (error.response?.status === 422) {
      const errors = error.response.data.errors || {};
      studentForm.value?.setErrors(errors);
      toast.error(t('students.form_correct_errors'));
    } else {
      toast.error(error.response?.data?.message || t('students.update_failed'));
    }
  } finally {
    isSubmitting.value = false;
  }
};

const handleCancel = () => {
  if (!isSubmitting.value) {
    router.push('/students');
  }
};

onMounted(async () => {
  try {
    const response = await api.get(`/students/${route.params.id}`, {
      params: {
        include: 'currentAssignment.class,currentAssignment.group,nationality'
      }
    });
    
    student.value = response.data.data;
  } catch (error) {
    console.error('Failed to load student:', error);
    toast.error(t('students.loading_student'));
  } finally {
    isLoading.value = false;
  }
});
</script>